/**
 * useEvents — Fetch events with optional filters.
 */

import { useState, useEffect, useCallback } from 'react';
import { getEvents, type Event } from '../api/client';

interface UseEventsOptions {
  machineId?: number;
  limit?: number;
  refreshInterval?: number;
}

interface UseEventsResult {
  events: Event[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useEvents(options: UseEventsOptions = {}): UseEventsResult {
  const { machineId, limit = 100, refreshInterval = 30_000 } = options;
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getEvents(machineId, limit);
      setEvents(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [machineId, limit]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [refresh, refreshInterval]);

  return { events, loading, error, refresh };
}
